import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { onAuthStateChange, signOut, updatePasswordFromRecovery } from '../lib/auth';
import {
  PASSWORD_RESET_SCOPES,
  hasRecoveryTokenInUrl,
  parsePasswordResetScope,
} from '../lib/passwordReset';
import { supabase } from '../supabase';
import PasswordInput from '../components/PasswordInput';
import { theme } from '../lib/theme';

const RECOVERY_SESSION_EVENTS = new Set([
  'PASSWORD_RECOVERY',
  'SIGNED_IN',
  'INITIAL_SESSION',
  'TOKEN_REFRESHED',
]);

export default function ResetPassword() {
  const { scopeParam } = useParams();
  const [searchParams] = useSearchParams();
  const scope = parsePasswordResetScope(scopeParam || searchParams.get('scope'));
  const meta = PASSWORD_RESET_SCOPES[scope];

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** @type {'verifying' | 'ready' | 'expired' | 'success'} */
  const [status, setStatus] = useState('verifying');

  useEffect(() => {
    let cancelled = false;
    let resolved = false;
    const startedWithRecoveryToken = hasRecoveryTokenInUrl();

    function markReady() {
      if (cancelled || resolved) return;
      resolved = true;
      setStatus('ready');
    }

    function markExpired() {
      if (cancelled || resolved) return;
      resolved = true;
      setStatus('expired');
    }

    const unsub = onAuthStateChange((event, session) => {
      if (session && RECOVERY_SESSION_EVENTS.has(event)) {
        markReady();
      }
    });

    async function waitForSession() {
      const attempts = startedWithRecoveryToken ? 40 : 8;
      for (let i = 0; i < attempts; i += 1) {
        if (cancelled || resolved) return;
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          markReady();
          return;
        }
        if (hasRecoveryTokenInUrl() || startedWithRecoveryToken) {
          await new Promise((r) => window.setTimeout(r, 250));
          continue;
        }
        break;
      }
    }

    void waitForSession();

    const failMs = startedWithRecoveryToken ? 15000 : 5000;
    const timer = window.setTimeout(() => {
      if (!cancelled && !resolved) {
        if (hasRecoveryTokenInUrl()) return;
        markExpired();
      }
    }, failMs);

    return () => {
      cancelled = true;
      unsub();
      window.clearTimeout(timer);
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { error: err } = await updatePasswordFromRecovery(password);
      if (err) {
        setError(err.message || 'Could not update password.');
        return;
      }
      try {
        await signOut();
      } catch {
        /* continue — password was saved */
      }
      setStatus('success');
    } catch (unexpected) {
      setError(unexpected?.message || 'Could not update password.');
    } finally {
      setBusy(false);
    }
  }

  const shell = {
    minHeight: '100vh',
    background: scope === 'marketplace' ? theme.bg : '#0f1419',
    color: scope === 'marketplace' ? theme.text : '#e8eaed',
    fontFamily: scope === 'marketplace' ? theme.font : "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  };

  const accent = scope === 'marketplace' ? theme.accent : '#0d7377';
  const muted = scope === 'marketplace' ? theme.muted : '#9aa0a6';

  if (status === 'verifying') {
    return (
      <div style={shell}>
        <p style={{ color: muted, margin: 0 }}>Verifying reset link…</p>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div style={shell}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>Link expired or invalid</h1>
          <p style={{ color: muted, fontSize: 15, lineHeight: 1.5 }}>
            Open the reset link from your email again, or request a new one.
          </p>
          <p style={{ marginTop: 24 }}>
            <Link to={meta.forgotPath} style={{ color: accent, fontWeight: 600, textDecoration: 'none' }}>
              Request a new reset link
            </Link>
          </p>
          <p style={{ marginTop: 16 }}>
            <Link to={meta.loginPath} style={{ color: muted, textDecoration: 'none', fontSize: 14 }}>
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={shell}>
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>Password updated</h1>
          <p style={{ color: muted, fontSize: 15, lineHeight: 1.5, marginBottom: 24 }}>
            Your new password is saved. Sign in with your new password to continue.
          </p>
          <Link
            to={`${meta.loginPath}?reset=done`}
            state={{ passwordReset: true }}
            style={{
              display: 'inline-block',
              padding: '14px 24px',
              background: accent,
              color: '#fff',
              borderRadius: 10,
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={shell}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Choose a new password</h1>
        <p style={{ color: muted, fontSize: 14, marginBottom: 24 }}>{meta.label}</p>
        <form onSubmit={handleSubmit}>
          <label style={{ fontSize: 13, color: muted }}>New password</label>
          <PasswordInput
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ marginTop: 6, marginBottom: 14 }}
            inputStyle={inputStyle(scope)}
            toggleColor={muted}
          />
          <label style={{ fontSize: 13, color: muted }}>Confirm password</label>
          <PasswordInput
            required
            minLength={8}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={{ marginTop: 6 }}
            inputStyle={inputStyle(scope)}
            toggleColor={muted}
          />
          {error ? <p style={{ color: '#f87171', fontSize: 14, marginTop: 12 }}>{error}</p> : null}
          <button
            type="submit"
            disabled={busy}
            style={{
              marginTop: 20,
              width: '100%',
              padding: 14,
              background: accent,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Saving…' : 'Save new password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function inputStyle(scope) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.12)',
    background: scope === 'marketplace' ? theme.bg1 : '#0f1419',
    color: scope === 'marketplace' ? theme.text : '#e8eaed',
    fontSize: 15,
  };
}
